import {action, makeAutoObservable, observable, runInAction} from "mobx";
import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {ILeague} from "data/types/entities";
import {Bindings} from "data/constants/bindings";
import type {ILeaguesStore} from "data/stores/leagues/leagues.store";
import {ILeaguesFilters} from "data/types/api";
import {RequestState} from "data/enums";

interface IProps {
	code: string | null;
}

export interface ILeaguesJoinController extends ViewController<IProps> {
	requestState: RequestState;

	joinLeague: (code: string) => void;

	loadMore: () => void;

	get leagues(): ILeague[];

	get isLoading(): boolean;

	get nextPage(): boolean;

	onFieldChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

@injectable()
export class LeaguesJoinController implements ILeaguesJoinController {
	@observable requestState = RequestState.IDLE;

	constructor(@inject(Bindings.LeaguesStore) private _leaguesStore: ILeaguesStore) {
		makeAutoObservable(this);
	}

	get isLoading(): boolean {
		return this._leaguesStore.isJoinLeaguesLoading;
	}

	get leagues(): ILeague[] {
		return this._leaguesStore.joinLeagues;
	}

	get nextPage(): boolean {
		return this._leaguesStore.loadMoreMapper["joinLeagues"];
	}

	get filters(): ILeaguesFilters {
		return this._leaguesStore.leagueFilters;
	}

	public loadMore = () => {
		void this._leaguesStore.fetchJoinLeaguesNext();
	};

	dispose(): void {
		return;
	}

	private updateFilters = (name: string, value: string) => {
		void this._leaguesStore.updateFilters({...this.filters, [name]: value});
	};

	public onFieldChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
		const {name, value} = event.currentTarget;

		if (!name) {
			return;
		}
		this.updateFilters(name, value);
	};

	@action
	public joinLeague = (code: string): void => {
		this.requestState = RequestState.PENDING;

		this._leaguesStore.joinLeague(code).finally(() => {
			runInAction(() => {
				this.requestState = RequestState.SUCCESS;
			});
		});
	};

	init({code}: IProps): void {
		this._leaguesStore.clearSelectedLeague();
		void this._leaguesStore.fetchJoinLeagues(code);
	}
}
